<div
  class="step-comments-popover bg-surface border-default flex min-h-0 w-full flex-col overflow-hidden rounded-lg border shadow-lg"
  (click)="$event.stopPropagation()"
  (keydown)="$event.stopPropagation()">
  <!-- Header -->
  <div class="flex shrink-0 items-start justify-between gap-2 px-3 pt-3">
    <div class="min-w-0 flex-1">
      <div class="flex items-center gap-2">
        <p class="text-text-primary min-w-0 truncate text-sm font-semibold">
          {{ title() }}
        </p>
        @if (resolved()) {
          <span
            class="bg-accent-interactive text-success flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold">
            <i [class]="ICONS.checkCircle"></i>
            {{ 'visualization.timeline.step_detail.resolved_chip' | translate }}
          </span>
        }
      </div>
      @if (task(); as t) {
        <p class="text-2xs text-text-tertiary mt-0.5 font-mono">
          {{ t.actionUid ?? kindKey() | translate }} · {{ t.startDate | date: 'HH:mm' }} –
          {{ t.endDate | date: 'HH:mm' }}
        </p>
      }
    </div>
    <button
      type="button"
      class="text-text-tertiary hover:text-text-primary shrink-0 cursor-pointer transition-colors"
      [attr.aria-label]="'visualization.timeline.step_detail.close' | translate"
      (click)="dismiss.emit()">
      <i [class]="ICONS.x"></i>
    </button>
  </div>

  <!-- Tab bar -->
  <div class="border-weak flex shrink-0 gap-1 border-b px-3 pt-2">
    @if (!commentsDisabled()) {
      <button
        type="button"
        role="tab"
        class="flex cursor-pointer items-center gap-1.5 border-b-2 px-2 pb-2 text-xs font-semibold transition-colors"
        [class.border-transparent]="activeTab() !== 'details'"
        [class.text-text-tertiary]="activeTab() !== 'details'"
        [class.text-text-primary]="activeTab() === 'details'"
        [style.border-bottom-color]="
          activeTab() === 'details' ? 'var(--accent-main)' : null
        "
        (click)="setTab('details')">
        <i [class]="ICONS.info"></i>
        {{ 'visualization.timeline.step_detail.tab_details' | translate }}
      </button>
      <button
        type="button"
        role="tab"
        class="flex cursor-pointer items-center gap-1.5 border-b-2 px-2 pb-2 text-xs font-semibold transition-colors"
        [class.border-transparent]="activeTab() !== 'comments'"
        [class.text-text-tertiary]="activeTab() !== 'comments'"
        [class.text-text-primary]="activeTab() === 'comments'"
        [style.border-bottom-color]="
          activeTab() === 'comments' ? 'var(--accent-main)' : null
        "
        (click)="setTab('comments')">
        <i [class]="ICONS.chatCircle"></i>
        {{ 'visualization.timeline.step_detail.tab_comments' | translate }}
        @if (comments().length > 0) {
          <span
            class="min-w-[15px] rounded-full px-1.5 text-center font-mono text-[10px] font-bold"
            [class.bg-accent-main]="activeTab() === 'comments'"
            [class.text-white]="activeTab() === 'comments'"
            [class.bg-default]="activeTab() !== 'comments'"
            [class.text-text-secondary]="activeTab() !== 'comments'">
            {{ comments().length }}
          </span>
        }
      </button>
    }
    @if (hasOccupation()) {
      <button
        type="button"
        role="tab"
        data-testid="tab-occupation"
        class="flex cursor-pointer items-center gap-1.5 border-b-2 px-2 pb-2 text-xs font-semibold transition-colors"
        [class.border-transparent]="activeTab() !== 'occupation'"
        [class.text-text-tertiary]="activeTab() !== 'occupation'"
        [class.text-text-primary]="activeTab() === 'occupation'"
        [style.border-bottom-color]="
          activeTab() === 'occupation' ? 'var(--accent-main)' : null
        "
        (click)="setTab('occupation')">
        <i [class]="ICONS.chartBar"></i>
        {{ 'visualization.occupation.tab' | translate }}
      </button>
    }
  </div>

  <!-- Scrollable content (between the tab bar and the composer) -->
  <div class="min-h-0 flex-1 overflow-y-auto">
    @if (activeTab() === 'occupation') {
      <div class="flex min-h-0 flex-col gap-2 px-3 pb-3">
        <div class="flex items-center justify-between gap-2">
          <span class="text-text-secondary text-[11px]">
            {{ 'visualization.occupation.form.' + occupationForm() | translate }}
          </span>
          <button
            type="button"
            class="text-text-tertiary hover:text-text-primary cursor-pointer transition-colors"
            [attr.aria-label]="'visualization.occupation.cycle_form' | translate"
            (click)="cycleOccupationForm()">
            <i [class]="ICONS.arrowsClockwise"></i>
          </button>
        </div>
        @if (occupationLoading()) {
          <p class="text-text-tertiary py-2 text-xs">
            {{ 'visualization.occupation.loading' | translate }}
          </p>
        } @else if (occupationSeries().length === 0) {
          <p class="text-text-tertiary py-2 text-xs">
            {{ 'visualization.occupation.empty' | translate }}
          </p>
        } @else {
          @if (!stopoverHoldsInfrastructure()) {
            <p
              data-testid="stopover-holds-nothing"
              class="border-default text-text-secondary rounded-sm border border-dashed px-2 py-1.5 text-[11px] leading-snug">
              {{ 'visualization.occupation.stopover_holds_nothing' | translate }}
            </p>
          }

          <app-occupation-legend
            surface="popup"
            [form]="occupationForm()"
            [marksOpenStep]="true" />

          <app-occupation-chart
            [series]="occupationSeries()"
            [form]="occupationForm()"
            [markWindowMs]="occupationMarkWindowMs()"
            [compact]="true" />
        }
      </div>
    } @else if (activeTab() === 'comments') {
      <div class="flex flex-col gap-2 p-3">
        @if (loading()) {
          <p class="text-text-tertiary py-1 text-xs">
            {{ 'visualization.timeline.step_detail.loading' | translate }}
          </p>
        } @else {
          @if (comments().length === 0) {
            <p class="text-text-tertiary py-1 text-xs">
              {{ 'visualization.timeline.step_detail.no_comments' | translate }}
            </p>
          } @else {
            <ul class="flex flex-col gap-3">
              @for (comment of comments(); track comment.id) {
                <li class="group flex items-start gap-2">
                  <!-- Avatar -->
                  <span
                    class="bg-brand-soft text-brand-deep flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold"
                    aria-hidden="true">
                    {{ initials(comment.createdBy.fullname) }}
                  </span>
                  <!-- Content column -->
                  <div class="min-w-0 flex-1">
                    <div class="flex items-center justify-between gap-2">
                      <span class="text-text-primary text-xs font-semibold">
                        {{ comment.createdBy.fullname }}
                      </span>
                      <span class="text-text-tertiary text-[10px]">
                        {{ comment.createdAt | timeAgo: currentLang() }}
                      </span>
                    </div>
                    @if (editingId() === comment.id) {
                      <div class="mt-1 flex items-center gap-1.5">
                        <input
                          type="text"
                          class="form-input flex-1 text-xs"
                          [disabled]="disabled()"
                          [value]="editDraft()"
                          (input)="onEditInput($event)"
                          (keydown.enter)="submitEdit(comment.id)" />
                        <button
                          type="button"
                          class="btn-primary shrink-0 px-2 py-1 text-xs"
                          [disabled]="disabled()"
                          (click)="submitEdit(comment.id)">
                          <i [class]="ICONS.check"></i>
                        </button>
                        <button
                          type="button"
                          class="text-text-tertiary hover:text-text-primary shrink-0 cursor-pointer"
                          (click)="cancelEdit()">
                          <i [class]="ICONS.x"></i>
                        </button>
                      </div>
                    } @else {
                      <div class="flex items-start justify-between gap-2">
                        <span class="text-text-secondary text-xs wrap-break-word">{{
                          comment.message
                        }}</span>
                        <div
                          class="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                          <button
                            type="button"
                            class="text-text-tertiary hover:text-text-primary cursor-pointer disabled:opacity-50"
                            [disabled]="disabled()"
                            [attr.aria-label]="
                              'visualization.timeline.step_detail.edit' | translate
                            "
                            (click)="startEdit(comment)">
                            <i [class]="ICONS.pencil" class="text-xs"></i>
                          </button>
                          <button
                            type="button"
                            class="text-text-tertiary cursor-pointer hover:text-red-500 disabled:opacity-50"
                            [disabled]="disabled()"
                            [attr.aria-label]="
                              'visualization.timeline.step_detail.remove' | translate
                            "
                            (click)="onRemove(comment.id)">
                            <i [class]="ICONS.trash" class="text-xs"></i>
                          </button>
                        </div>
                      </div>
                    }
                  </div>
                </li>
              }
            </ul>
          }
        }
      </div>
    } @else {
      @if (task(); as t) {
        <dl class="flex flex-col p-3 text-xs">
          <div
            class="group/row border-weak relative flex items-baseline justify-between gap-3 border-b py-1.5">
            <dt class="text-text-tertiary">
              {{ 'visualization.timeline.step_detail.detail_time' | translate }}
            </dt>
            <dd class="text-text-primary font-mono font-medium select-text">
              {{ t.startDate | date: 'shortTime' : undefined : currentLang() }} –
              {{ t.endDate | date: 'shortTime' : undefined : currentLang() }}
            </dd>
            @if (isMetaPressed()) {
              <div
                class="absolute top-1/2 right-0 flex h-4 -translate-y-1/2 items-center justify-center rounded-md border border-zinc-300/20 bg-zinc-50 p-0.5 opacity-0 shadow-sm transition-opacity group-hover/row:opacity-100 dark:bg-zinc-800">
                <button
                  type="button"
                  tabindex="-1"
                  class="flex h-4 w-4 cursor-pointer items-center justify-center rounded-[calc(var(--radius-md)-var(--spacing)/2)] p-0.5 hover:bg-zinc-300 dark:hover:bg-zinc-900"
                  [attr.aria-label]="'table.cell.overlay_actions.copy.tooltip' | translate"
                  (click)="copyToClipboard(timeValueForCopy(t))">
                  <i class="text-zinc-500 dark:text-zinc-200" [class]="ICONS.copy"></i>
                </button>
              </div>
            }
          </div>
          <div
            class="group/row border-weak relative flex items-baseline justify-between gap-3 border-b py-1.5">
            <dt class="text-text-tertiary">
              {{ 'visualization.timeline.step_detail.detail_duration' | translate }}
            </dt>
            <dd class="text-text-primary font-mono font-medium select-text">
              {{ formatedDuration() }}
            </dd>
            @if (isMetaPressed()) {
              <div
                class="absolute top-1/2 right-0 flex h-4 -translate-y-1/2 items-center justify-center rounded-md border border-zinc-300/20 bg-zinc-50 p-0.5 opacity-0 shadow-sm transition-opacity group-hover/row:opacity-100 dark:bg-zinc-800">
                <button
                  type="button"
                  tabindex="-1"
                  class="flex h-4 w-4 cursor-pointer items-center justify-center rounded-[calc(var(--radius-md)-var(--spacing)/2)] p-0.5 hover:bg-zinc-300 dark:hover:bg-zinc-900"
                  [attr.aria-label]="'table.cell.overlay_actions.copy.tooltip' | translate"
                  (click)="copyToClipboard(formatedDuration())">
                  <i class="text-zinc-500 dark:text-zinc-200" [class]="ICONS.copy"></i>
                </button>
              </div>
            }
          </div>
          @if (t.siteUid) {
            <div
              class="group/row border-weak relative flex items-baseline justify-between gap-3 border-b py-1.5">
              <dt class="text-text-tertiary">
                {{ 'visualization.timeline.step_detail.detail_site' | translate }}
              </dt>
              <dd class="text-text-primary font-mono font-medium break-all select-text">
                {{ t.siteUid }}
              </dd>
              @if (isMetaPressed()) {
                <div
                  class="absolute top-1/2 right-0 flex h-4 -translate-y-1/2 items-center justify-center rounded-md border border-zinc-300/20 bg-zinc-50 p-0.5 opacity-0 shadow-sm transition-opacity group-hover/row:opacity-100 dark:bg-zinc-800">
                  <button
                    type="button"
                    tabindex="-1"
                    class="flex h-4 w-4 cursor-pointer items-center justify-center rounded-[calc(var(--radius-md)-var(--spacing)/2)] p-0.5 hover:bg-zinc-300 dark:hover:bg-zinc-900"
                    [attr.aria-label]="
                      'table.cell.overlay_actions.copy.tooltip' | translate
                    "
                    (click)="copyToClipboard(t.siteUid!)">
                    <i class="text-zinc-500 dark:text-zinc-200" [class]="ICONS.copy"></i>
                  </button>
                </div>
              }
            </div>
          }
          @if (t.actionIndex !== undefined) {
            <div
              class="group/row border-weak relative flex items-baseline justify-between gap-3 border-b py-1.5">
              <dt class="text-text-tertiary">
                {{ 'visualization.timeline.step_detail.detail_step' | translate }}
              </dt>
              <dd class="text-text-primary font-mono font-medium select-text">
                {{ t.actionIndex + 1 }}
              </dd>
              @if (isMetaPressed()) {
                <div
                  class="absolute top-1/2 right-0 flex h-4 -translate-y-1/2 items-center justify-center rounded-md border border-zinc-300/20 bg-zinc-50 p-0.5 opacity-0 shadow-sm transition-opacity group-hover/row:opacity-100 dark:bg-zinc-800">
                  <button
                    type="button"
                    tabindex="-1"
                    class="flex h-4 w-4 cursor-pointer items-center justify-center rounded-[calc(var(--radius-md)-var(--spacing)/2)] p-0.5 hover:bg-zinc-300 dark:hover:bg-zinc-900"
                    [attr.aria-label]="
                      'table.cell.overlay_actions.copy.tooltip' | translate
                    "
                    (click)="copyToClipboard((t.actionIndex! + 1).toString())">
                    <i class="text-zinc-500 dark:text-zinc-200" [class]="ICONS.copy"></i>
                  </button>
                </div>
              }
            </div>
          }
          @for (label of t.labels ?? []; track $index) {
            <div
              class="group/row border-weak relative flex items-baseline justify-between gap-3 border-b py-1.5 last:border-none">
              <dt class="text-text-tertiary">{{ label.label }}</dt>
              <dd class="text-text-primary font-mono font-medium break-all select-text">
                {{ label.value }}
              </dd>
              @if (isMetaPressed()) {
                <div
                  class="absolute top-1/2 right-0 flex h-4 -translate-y-1/2 items-center justify-center rounded-md border border-zinc-300/20 bg-zinc-50 p-0.5 opacity-0 shadow-sm transition-opacity group-hover/row:opacity-100 dark:bg-zinc-800">
                  <button
                    type="button"
                    tabindex="-1"
                    class="flex h-4 w-4 cursor-pointer items-center justify-center rounded-[calc(var(--radius-md)-var(--spacing)/2)] p-0.5 hover:bg-zinc-300 dark:hover:bg-zinc-900"
                    [attr.aria-label]="
                      'table.cell.overlay_actions.copy.tooltip' | translate
                    "
                    (click)="copyToClipboard(label.value)">
                    <i class="text-zinc-500 dark:text-zinc-200" [class]="ICONS.copy"></i>
                  </button>
                </div>
              }
            </div>
          }
        </dl>
      }
    }
  </div>

  <!-- Composer (pinned at the bottom of the popup) -->
  @if (activeTab() === 'comments' && !loading()) {
    <div class="border-weak shrink-0 border-t p-3">
      <div class="flex flex-col gap-2">
        @if (comments().length > 0) {
          <app-button
            [type]="resolved() ? 'success' : 'cancel'"
            [disabled]="disabled()"
            [icon]="resolved() ? ICONS.arrowsClockwise : ICONS.checkCircle"
            [label]="
              (resolved()
                ? 'visualization.timeline.step_detail.reopen'
                : 'visualization.timeline.step_detail.resolve'
              ) | translate
            "
            (clickEvent)="onToggleResolve()" />
        }
        <div class="flex items-end gap-1.5">
          <textarea
            rows="2"
            class="form-textarea min-h-[2.75rem] flex-1 resize-none text-xs"
            [disabled]="disabled()"
            [value]="draft()"
            [placeholder]="'visualization.timeline.step_detail.add_placeholder' | translate"
            (input)="onInput($event)"
            (keydown)="onKeyDown($event)"></textarea>
          <app-button
            type="primary"
            [disabled]="disabled() || !canSubmit()"
            [attr.aria-label]="'visualization.timeline.step_detail.add' | translate"
            [icon]="ICONS.paperPlaneTilt"
            (clickEvent)="submit()" />
        </div>
      </div>
    </div>
  }
</div>
